import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { retryFailedFontFamilyAssets } from "@/fonts/fontRegistry";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import styles from "./TextFontMenu.module.css";
import { TextFontMenuContent } from "./TextFontMenuContent";
import { useRecentTextFonts } from "./useRecentTextFonts";

interface TextFontMenuProps {
    activeSubMenu: ActiveSubMenuState;
    setActiveSubMenu: SetActiveSubMenu;
}

export function TextFontMenu({ activeSubMenu, setActiveSubMenu }: TextFontMenuProps) {
    const options = useAtomValue(widgetMenuOptionsByNameAtom("textFont"));
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);
    const { recentFonts, addRecentFont } = useRecentTextFonts();

    const handleButtonClick = useCallback(
        async (fontFamily: string) => {
            const setNewTextFontOptions = setNewOptions({ name: "textFont", setWidgetMenuOptions });
            retryFailedFontFamilyAssets(fontFamily);
            const isApplied = await applyValue({
                options,
                value: fontFamily,
                setNewOptions: setNewTextFontOptions,
            });

            if (isApplied) {
                addRecentFont(fontFamily);
                setActiveSubMenu(null);
            }
        },
        [options, addRecentFont, setWidgetMenuOptions, setActiveSubMenu],
    );

    return (
        <SubMenu
            activeSubMenu={activeSubMenu}
            align="stretch"
            className={styles.wrapper}
            alignToMenuLeft
        >
            <TextFontMenuContent
                recentFonts={recentFonts}
                onTextFontButtonClick={handleButtonClick}
            />
        </SubMenu>
    );
}
